import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Briefcase, Shield, Target, BarChart3, FileCheck, Award, Users, Building2, Megaphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, heroImages } from '../data/mockData';

const Services = () => {
  const iconMap = {
    TrendingUp, Briefcase, Shield, Target, BarChart3, FileCheck, Award, Users, Building2, Megaphone
  };

  const serviceCategories = {
    finance: { name: 'Finance & Investment', color: 'red' },
    management: { name: 'Management & Strategy', color: 'blue' },
    compliance: { name: 'Compliance & Advisory', color: 'green' },
    hr: { name: 'Human Resources', color: 'purple' },
    setup: { name: 'Business Setup', color: 'orange' },
    marketing: { name: 'Corporate Communications', color: 'pink' }
  };

  return (
    <div className="min-h-screen pt-28 md:pt-36">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImages.services}
            alt="Our Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/60"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Comprehensive Business <span className="text-red-500">Solutions</span>
            </h1>
            <p className="text-xl text-gray-300">
              End-to-end advisory and execution services tailored to your business objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Service Portfolio
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From strategic planning to hands-on execution, we deliver specialized expertise across all aspects of your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon] || Target;
              
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-gray-200">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex-shrink-0">
                        <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-600 transition-colors">
                          <IconComponent className="w-7 h-7 text-red-600 group-hover:text-white transition-colors" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                        <span className="inline-block text-xs font-semibold text-red-600 bg-red-50 px-3 py-1 rounded-full">
                          {serviceCategories[service.category]?.name || 'Advisory'}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-start text-sm text-gray-600">
                            <span className="w-1.5 h-1.5 bg-red-600 rounded-full mr-3 mt-1.5 flex-shrink-0"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Link to="/contact">
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white group-hover:bg-red-700">
                        Request Service Details
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Engagement Process
              </h2>
              <p className="text-lg text-gray-600">
                A structured, collaborative approach ensuring alignment and measurable results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Discovery</h3>
                <p className="text-sm text-gray-600">
                  Understand your challenges, objectives, and requirements
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Strategy</h3>
                <p className="text-sm text-gray-600">
                  Develop tailored solutions and implementation roadmap
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Execution</h3>
                <p className="text-sm text-gray-600">
                  Hands-on implementation with continuous monitoring
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Optimization</h3>
                <p className="text-sm text-gray-600">
                  Ongoing support and continuous improvement
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Let's discuss which services align best with your business objectives and how we can drive results together.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 h-12">
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
